import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Editor as TinyEditor } from "@tinymce/tinymce-react";
import Editor, { useMonaco } from "@monaco-editor/react";
import { toast } from "react-toastify";
import {
  EmailTemplateAsset,
  validateEmailTemplate,
} from "../../api/siteSettings";
import {
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  HStack,
  VStack,
  isEsopViewer,
} from "../../components/utils";
import { Input, Label } from "../../components/shared/InputField";
import { determineUserAccessToResource } from "../../utils/auth";
import {
  Action as DefaultAction,
  Resource,
} from "../../utils/interfaces/Companies";
import { usePermissionStore } from "../../store";
import {
  useDeleteEmailTemplateAsset,
  useGetEmailTemplateAssets,
  useUploadEmailTemplateAsset,
} from "../../queries/siteSettings";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TINYMCE_API_KEY = "l8zomkw48zb8qhvwpahb2g5nodmnma6qjyz1acf2y14t53z6";
const PREVIEW_DEBOUNCE_MS = 400;

const TEMPLATE_VARIABLES = [
  { label: "User Name", value: "#user.name#" },
  { label: "Employee Name", value: "#employee.name#" },
  { label: "Grant Number", value: "#grant.number#" },
  { label: "Company Name", value: "#company.name#" },
  { label: "Vesting Date", value: "#vesting.date#" },
  { label: "Exercise Price", value: "#exercise.price#" },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CloseEvent {
  editedData?: string;
  subject?: string;
}

interface DialogProps {
  templateName: string;
  templateContent: string;
  templateType: string;
  onClose: (data: CloseEvent) => void;
  subject?: string;
}

interface SplitTemplateContent {
  templateHeader: string;
  templateBody: string;
  templateFooter: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function EmailTemplateEditorEnhanced(props: DialogProps) {
  const { templateName, templateContent, templateType, onClose, subject } =
    props;

  const { permission } = usePermissionStore();
  const isUserEsopViewer = isEsopViewer();
  const monaco = useMonaco();

  // Editor state
  const [editMode, setEditMode] = useState<"Classic" | "HTML">("Classic");
  const [emailSubject, setEmailSubject] = useState(subject);
  const [splitContent, setSplitContent] = useState<SplitTemplateContent>({
    templateHeader: "",
    templateBody: "",
    templateFooter: "",
  });
  const [currentBody, setCurrentBody] = useState("");
  const [saving, setSaving] = useState(false);

  // Asset state (React Query)
  const { data: assets = [], isLoading: assetsLoading } =
    useGetEmailTemplateAssets();
  const { mutate: uploadAsset, isLoading: assetUploading } =
    useUploadEmailTemplateAsset();
  const { mutate: deleteAsset } = useDeleteEmailTemplateAsset();
  const [deletingAssetId, setDeletingAssetId] = useState<string | null>(null);
  // Keep a ref so TinyMCE fetch closures always see the latest list
  const assetsRef = useRef<EmailTemplateAsset[]>([]);

  // Preview state
  const [previewHtml, setPreviewHtml] = useState("");
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // DOM refs
  const tinyEditorRef = useRef<TinyEditor["editor"]>();
  const assetInputRef = useRef<HTMLInputElement>(null);

  // Monaco editor options (typed to avoid TS widening issues)
  const monacoEditorOptions = {
    autoClosingBrackets: "always" as const,
    inlineSuggest: { enabled: true, mode: "prefix" as const },
    language: "html" as const,
    suggest: { insertMode: "insert" as const },
    suggestSelection: "first" as const,
  };

  // ---------------------------------------------------------------------------
  // Initialisation
  // ---------------------------------------------------------------------------

  // Split templateContent into header / body / footer on mount
  useEffect(() => {
    if (!templateContent) return;
    const BODY_START = "<!-- body start -->";
    const BODY_END = "<!-- body end -->";
    const headerIdx = templateContent.indexOf(BODY_START);
    const footerIdx = templateContent.indexOf(BODY_END);
    const header =
      headerIdx >= 0 ? templateContent.substring(0, headerIdx) : "";
    const footer = footerIdx >= 0 ? templateContent.substring(footerIdx) : "";
    const body =
      headerIdx >= 0 && footerIdx >= 0
        ? templateContent.substring(headerIdx, footerIdx)
        : templateContent;
    setSplitContent({
      templateHeader: header,
      templateBody: body,
      templateFooter: footer,
    });
    setCurrentBody(body);
  }, [templateContent]);

  // ---------------------------------------------------------------------------
  // Live preview (debounced)
  // ---------------------------------------------------------------------------

  const buildPreview = useCallback(
    (body: string) => {
      const { templateHeader, templateFooter } = splitContent;
      return templateHeader + body + templateFooter;
    },
    [splitContent]
  );

  const schedulePreviewUpdate = useCallback(
    (body: string) => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
      previewTimerRef.current = setTimeout(() => {
        setPreviewHtml(buildPreview(body));
      }, PREVIEW_DEBOUNCE_MS);
    },
    [buildPreview]
  );

  // Rebuild preview whenever body or split content changes
  useEffect(() => {
    schedulePreviewUpdate(currentBody);
    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
  }, [currentBody, schedulePreviewUpdate]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const getCurrentBody = (): string => {
    if (editMode === "Classic") {
      return tinyEditorRef.current?.getContent() ?? currentBody;
    }
    return monaco?.editor?.getEditors()[0]?.getValue() ?? currentBody;
  };

  // Preserve content when switching editor modes
  const handleModeChange = (mode: "Classic" | "HTML") => {
    if (mode === editMode) return;
    const body = getCurrentBody();
    setCurrentBody(body);
    setEditMode(mode);
  };

  // ---------------------------------------------------------------------------
  // Save / Close
  // ---------------------------------------------------------------------------

  const tearDownEditorAndClose = () => {
    const body = getCurrentBody();
    const editedContent =
      splitContent.templateHeader + body + splitContent.templateFooter;
    onClose({ editedData: editedContent, subject: emailSubject });
  };

  const validateAndSave = () => {
    const body = getCurrentBody();
    if (!body) return;
    setSaving(true);
    const full =
      splitContent.templateHeader + body + splitContent.templateFooter;
    const base64Body = btoa(unescape(encodeURIComponent(full)));
    validateEmailTemplate(base64Body, templateType)
      .then(() => tearDownEditorAndClose())
      .catch((err) => {
        toast(err.response?.data?.errorRaw || "Template validation failed", {
          type: "error",
          autoClose: 10000,
        });
      })
      .finally(() => setSaving(false));
  };

  // ---------------------------------------------------------------------------
  // Asset upload / insert
  // ---------------------------------------------------------------------------

  const insertAsset = (url: string, name: string) => {
    const imgHtml = `<img src="${url}" alt="${name}" style="max-width:100%;height:auto;" />`;
    if (editMode === "Classic") {
      tinyEditorRef.current?.insertContent(imgHtml);
    } else {
      const monacoEditor = monaco?.editor?.getEditors()[0];
      if (monacoEditor) {
        const sel = monacoEditor.getSelection();
        monacoEditor.executeEdits("insert-asset", [
          {
            range: sel ?? {
              startLineNumber: 1,
              startColumn: 1,
              endLineNumber: 1,
              endColumn: 1,
            },
            text: imgHtml,
          },
        ]);
      }
    }
  };

  const handleAssetDelete = (assetId: string) => {
    setDeletingAssetId(assetId);
    deleteAsset(assetId, {
      onSuccess: () => {
        toast("Asset deleted", { type: "success", autoClose: 2000 });
      },
      onError: () => {
        toast("Failed to delete asset. Please try again.", { type: "error" });
      },
      onSettled: () => {
        setDeletingAssetId(null);
      },
    });
  };

  const handleAssetUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    uploadAsset(file, {
      onSuccess: () => {
        toast("Asset uploaded successfully", {
          type: "success",
          autoClose: 3000,
        });
      },
      onError: () => {
        toast("Failed to upload asset. Please try again.", { type: "error" });
      },
    });
  };

  // ---------------------------------------------------------------------------
  // Monaco completion provider (Advanced mode)
  // ---------------------------------------------------------------------------

  function handleMonacoEditorDidMount(editor: any, monacoInstance: any) {
    if (!monacoInstance) return;
    monacoInstance.languages.registerCompletionItemProvider("html", {
      triggerCharacters: ["?"],
      provideCompletionItems: (model: any, position: any) => {
        const wordBefore = model.getWordUntilPosition({
          lineNumber: position.lineNumber,
          column: position.column - 1,
        });
        const wordUntil = model.getWordUntilPosition(position);
        if (wordBefore.word.trim() === "" || wordUntil.word.trim() === "#") {
          const keywords = [
            {
              label: "Username",
              insertText: "#user.name#",
              description: "Name of the user",
              kind: monacoInstance.languages.CompletionItemKind.Text,
              insertTextRules:
                monacoInstance.languages.CompletionItemInsertTextRule
                  .InsertAsSnippet,
            },
            {
              label: "Employee name",
              insertText: "#employee.name#",
              description: "Name of the Employee",
              kind: monacoInstance.languages.CompletionItemKind.Text,
              insertTextRules:
                monacoInstance.languages.CompletionItemInsertTextRule
                  .InsertAsSnippet,
            },
            {
              label: "Grant Number",
              insertText: "#grant.number#",
              description: "Grant identifier",
              kind: monacoInstance.languages.CompletionItemKind.Text,
              insertTextRules:
                monacoInstance.languages.CompletionItemInsertTextRule
                  .InsertAsSnippet,
            },
            {
              label: "Company Name",
              insertText: "#company.name#",
              description: "Name of the Company",
              kind: monacoInstance.languages.CompletionItemKind.Text,
              insertTextRules:
                monacoInstance.languages.CompletionItemInsertTextRule
                  .InsertAsSnippet,
            },
          ];
          const suggestions = keywords.map((kw) => ({
            label: kw.label,
            kind: kw.kind,
            documentation: kw.description,
            insertText: kw.insertText,
            detail: kw.description,
            insertTextRules: kw.insertTextRules,
            range: {
              startLineNumber: position.lineNumber,
              startColumn: wordUntil.startColumn,
              endLineNumber: position.lineNumber,
              endColumn: wordUntil.endColumn - 1,
            },
          }));
          return { suggestions };
        }
        return { suggestions: [] };
      },
    });
  }

  // ---------------------------------------------------------------------------
  // TinyMCE init config
  // ---------------------------------------------------------------------------

  // Keep assetsRef current on every render so TinyMCE fetch closures always
  // read the latest list without needing to re-initialise the editor.
  assetsRef.current = assets;

  // useMemo ensures TinyMCE never sees a new `init` reference, which would
  // cause it to re-mount and attempt to re-register custom buttons (failing
  // silently on the second registration, leaving the Assets menu empty).
  const tinyMceInit = useMemo(
    () => ({
      height: 500,
      menubar: false,
      plugins: "link image lists table code",
      toolbar:
        "undo redo | formatselect | bold italic underline | alignleft aligncenter alignright | bullist numlist | link",
      automatic_uploads: false,
      image_advtab: true,
      content_style:
        "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
      // Keep uploaded images embedded as data-URLs (no CDN upload required)
      images_upload_handler: (blobInfo: any) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(blobInfo.blob());
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () =>
            // eslint-disable-next-line prefer-promise-reject-errors
            reject({ message: "Image upload failed", remove: true });
        }),
      setup: (editor: any) => {
        // --- Template variable dropdown ---
        editor.ui.registry.addMenuButton("insertVariable", {
          text: "Variables",
          tooltip: "Insert template variable",
          fetch: (callback: (items: any[]) => void) => {
            const items = TEMPLATE_VARIABLES.map((v) => ({
              type: "menuitem" as const,
              text: v.label,
              onAction: () => editor.insertContent(v.value),
            }));
            callback(items);
          },
        });

        // --- Insert asset menu button ---
        editor.ui.registry.addMenuButton("insertAsset", {
          text: "Assets",
          icon: "image",
          tooltip: "Insert an uploaded asset",
          fetch: (callback: (items: any[]) => void) => {
            const list = assetsRef.current;
            const items =
              list.length > 0
                ? list.map((asset) => ({
                    type: "menuitem" as const,
                    text: asset.name,
                    onAction: () =>
                      editor.insertContent(
                        `<img src="${asset.s3Path}" alt="${asset.name}" style="max-width:100%;height:auto;" />`
                      ),
                  }))
                : [
                    {
                      type: "menuitem" as const,
                      text: "No assets uploaded yet",
                      onAction: () => {},
                    },
                  ];
            callback(items);
          },
        });
      },
    }),
    []
  ); // empty deps: setup runs once; assetsRef.current is read at call-time

  // ---------------------------------------------------------------------------
  // Permission guard
  // ---------------------------------------------------------------------------

  const canEdit = determineUserAccessToResource(
    permission?.aclList || [],
    Resource.CompanySettings,
    DefaultAction.All
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col">
      {/* Dialog header */}
      <div className="p-4 px-10 mt-4 ml-3 text-lg font-medium border-b">
        <h6 className="flex justify-between items-center">
          Edit {templateName}
          <span
            onClick={() => onClose({})}
            className="cursor-pointer text-base"
          >
            ✕
          </span>
        </h6>
      </div>

      <VStack className="gap-4 p-4 px-10">
        {/* ── Editor Mode Toggle ── */}
        <HStack className="items-center gap-4">
          <span className="text-sm font-medium text-gray-600">
            Editor Mode:
          </span>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              className="accent-orange-500 cursor-pointer"
              checked={editMode === "Classic"}
              onChange={() => handleModeChange("Classic")}
            />
            <span className="text-sm">Classic</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <input
              type="radio"
              className="accent-orange-500 cursor-pointer"
              checked={editMode === "HTML"}
              onChange={() => handleModeChange("HTML")}
            />
            <span className="text-sm">Advanced (HTML)</span>
          </label>
        </HStack>

        {/* ── Email Subject ── */}
        <HStack className="items-center gap-2">
          <Label className="whitespace-nowrap mt-0">Subject:</Label>
          <Input
            className="flex-1 px-2 py-2"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            placeholder="Email subject"
          />
        </HStack>

        {/* ── Three-column pane: Assets | Editor | Live Preview ── */}
        <HStack className="items-start gap-4 w-full">
          {/* Assets column */}
          <div
            className="flex flex-col w-[200px] shrink-0 border rounded-lg bg-gray-50 overflow-hidden"
            style={{ height: "500px" }}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b bg-white shrink-0">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Assets
              </span>
              <ButtonPrimary1
                disabled={assetUploading || isUserEsopViewer || !canEdit}
                onClick={() => assetInputRef.current?.click()}
                loading={assetUploading}
              >
                Upload
              </ButtonPrimary1>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {assetsLoading ? (
                <p className="text-xs text-gray-400 mt-2">Loading…</p>
              ) : assets.length === 0 ? (
                <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                  No assets yet. Upload images to insert them into templates.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {assets.map((asset) => (
                    <div
                      key={asset.id}
                      className="group relative border rounded bg-white overflow-hidden cursor-pointer hover:border-orange-400 transition-colors"
                      title={`Click to insert: ${asset.name}`}
                      onClick={() => insertAsset(asset.s3Path, asset.name)}
                    >
                      <img
                        src={asset.s3Path}
                        alt={asset.name}
                        className="object-contain w-full h-16 p-1"
                      />
                      <div className="px-1 pb-1 truncate text-xs text-gray-500 text-center">
                        {asset.name}
                      </div>
                      {/* Hover overlay: Insert label */}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity pointer-events-none">
                        <span className="text-white text-xs font-medium">
                          Insert
                        </span>
                      </div>
                      {/* Delete icon — top-right corner */}
                      {canEdit && !isUserEsopViewer && (
                        <button
                          type="button"
                          disabled={deletingAssetId === asset.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAssetDelete(asset.id);
                          }}
                          title="Delete asset"
                          className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 w-5 h-5 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white disabled:opacity-60 transition-opacity"
                        >
                          {deletingAssetId === asset.id ? (
                            <span className="text-[10px] leading-none">…</span>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-3 h-3"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Hidden file input */}
            <input
              ref={assetInputRef}
              type="file"
              accept=".png,.jpg,.jpeg,.gif,.svg,.webp"
              className="hidden"
              onChange={handleAssetUpload}
            />
          </div>

          {/* Editor column */}
          <div className="flex-1 min-w-0">
            {editMode === "Classic" && (
              <TinyEditor
                apiKey={TINYMCE_API_KEY}
                onInit={(_evt, editor) => {
                  tinyEditorRef.current = editor;
                }}
                initialValue={splitContent.templateBody}
                onEditorChange={(content) => setCurrentBody(content)}
                init={tinyMceInit}
              />
            )}
            {editMode === "HTML" && (
              <Editor
                onMount={handleMonacoEditorDidMount}
                options={monacoEditorOptions}
                language="html"
                height="500px"
                defaultLanguage="html"
                theme=""
                value={currentBody}
                onChange={(value) => setCurrentBody(value ?? "")}
              />
            )}
          </div>

          {/* Live Preview column */}
          <div className="flex flex-col w-[420px] shrink-0 border rounded-lg overflow-hidden hidden">
            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b bg-gray-50">
              Live Preview
            </div>
            <iframe
              srcDoc={
                previewHtml ||
                "<div style='padding:16px;color:#aaa;font-family:sans-serif;'>Start typing to see a preview…</div>"
              }
              title="Email Preview"
              className="w-full border-0"
              style={{ height: "500px" }}
              sandbox="allow-same-origin"
            />
          </div>
        </HStack>

        {/* ── Action Buttons ── */}
        {!isUserEsopViewer && (
          <HStack className="flex-row-reverse gap-4">
            <ButtonPrimary
              className={`${
                !canEdit ? "opacity-50 cursor-not-allowed" : ""
              } flex items-center justify-center`}
              onClick={validateAndSave}
              disabled={saving || !canEdit}
            >
              {saving ? "Validating…" : "Save"}
            </ButtonPrimary>
            <ButtonSecondary
              onClick={() => onClose({})}
              className="text-gray-400 bg-slate-50"
            >
              Cancel
            </ButtonSecondary>
          </HStack>
        )}
      </VStack>
    </div>
  );
}

export default EmailTemplateEditorEnhanced;
